import { compare } from 'compare-versions'
import type { DailyTask } from './focus-challenge-model'

// TEMPORARY workaround (see docs/adr/0025): native lets a user plant a tree
// type they have not unlocked when a daily-challenge deeplink hands it to
// them, so the web hides that deeplink arrow unless ownership is confirmed.
// Removal path: fill in the app version that ships the native fix below —
// from that version on the gate (and its ownership request) vanishes on its
// own, and once the fixed version is the floor this whole module can be
// deleted. Pattern shared with special-challenge-model's app-version gate.
export const NATIVE_TREE_OWNERSHIP_FIX_VERSION: string | null = null

export type TreeOwnershipState =
  | { status: 'success'; unlockedGids: ReadonlySet<number> }
  | { status: 'error' }
  | { status: 'pending' }

export type TreeArrowDecision =
  | { visible: true }
  // 'not_owned' and 'ownership_unknown' are diagnostics-worthy;
  // 'ownership_pending' is the transient loading state and is not reported.
  | { visible: false; reason: 'not_owned' | 'ownership_unknown' }
  | { visible: false; reason: 'ownership_pending' }

export function isTreeOwnershipGateActive(
  appVersion: string,
  fixVersion: string | null = NATIVE_TREE_OWNERSHIP_FIX_VERSION,
): boolean {
  if (fixVersion === null) return true
  return compare(appVersion, fixVersion, '<')
}

// The arrow's pre-existing visibility rule — a locked chest with a deeplink —
// shared by the render, the laziness predicate, and the diagnostics guard so
// the three can't drift apart.
export function hasDeeplinkArrowSurface(task: DailyTask): boolean {
  return (
    task.chestState === 'locked' &&
    !!task.progress.challenge_difficulty.deeplink
  )
}

// Whether the ownership request is needed at all: only when some daily card
// would actually show a tree-scoped deeplink arrow. On days without such a
// challenge the gate costs zero requests.
export function needsTreeOwnership(
  tasks: DailyTask[],
  gateActive: boolean,
): boolean {
  if (!gateActive) return false
  return tasks.some(
    (t) =>
      hasDeeplinkArrowSurface(t) &&
      t.progress.challenge_difficulty.tree_type != null,
  )
}

// Fail closed: the asymmetry is that wrongly hiding the arrow costs the user
// a shortcut they can walk themselves, while wrongly showing it hands out a
// tree they never unlocked. So anything short of confirmed ownership hides.
export function decideTreeArrow(input: {
  // The challenge's tree gid (challenge_difficulty.tree_type); null on a
  // tree-less challenge, which the gate never applies to.
  gid: number | null
  gateActive: boolean
  ownership: TreeOwnershipState
}): TreeArrowDecision {
  if (input.gid == null || !input.gateActive) return { visible: true }
  if (input.ownership.status === 'success') {
    return input.ownership.unlockedGids.has(input.gid)
      ? { visible: true }
      : { visible: false, reason: 'not_owned' }
  }
  if (input.ownership.status === 'error') {
    return { visible: false, reason: 'ownership_unknown' }
  }
  return { visible: false, reason: 'ownership_pending' }
}
